import { skipToken, useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { fileProblem, type AssetKind, type AssetKindView, type GameRef } from '../domain/assets.js';
import { putWithProgress } from '../utils/putWithProgress.js';
import { useTRPC, useTRPCClient } from '../utils/trpc.js';

export type { AssetEntry } from '../domain/assets.js';

export type AssetRef = { key: GameRef; kind: AssetKind; name: string };

/** Who still points at one file — the delete dialog's first question. */
export const useAssetReferences = (ref: AssetRef) => {
    const trpc = useTRPC();
    return useQuery(trpc.assets.references.queryOptions(ref));
};

/** Deletes one file everywhere at once. Callers attach their own `onSuccess`/`onError` per call — a dialog closing on success, refusing on a reference that just appeared, is its own business. Quiet: the dialog reports the failure. */
export const useDeleteAsset = () => {
    const trpc = useTRPC();
    return useMutation({ ...trpc.assets.remove.mutationOptions(), meta: { quiet: true } });
};

/** Whether the signed-in user holds `assets:write` — the one gate for uploading or deleting a game's files. */
export const useCanWriteAssets = (): boolean => {
    const trpc = useTRPC();
    // `staleTime: Infinity` — see `hooks/useWriteAccess.ts`.
    const me = useQuery({ ...trpc.users.me.queryOptions(), staleTime: Infinity });
    return me.data?.permissions.includes('assets:write') ?? false;
};

/**
 * Whether asset storage is configured, and the kind registry the client renders from
 * (`assets.status` — the client never imports `@backoffice/*` at runtime). `meta: { quiet: true }`:
 * a user without `assets:read` just doesn't see assets, no toast.
 */
export const useAssetStatus = () => {
    const trpc = useTRPC();
    // `staleTime: Infinity` — see `hooks/useWriteAccess.ts`.
    const query = useQuery({ ...trpc.assets.status.queryOptions(), meta: { quiet: true }, staleTime: Infinity });
    const kinds = query.data?.kinds ?? [];
    return {
        enabled: !query.isLoading && !query.isError && (query.data?.enabled ?? false),
        kinds,
        isLoading: query.isLoading,
        kindOf: (name: string): AssetKindView | undefined => kinds.find((k) => k.name === name),
    };
};

/** A game's files of one kind. Skipped while the game or the kind is not yet known, or storage is off. */
export const useAssetList = (game: GameRef | null, kind: AssetKind | null) => {
    const trpc = useTRPC();
    const status = useAssetStatus();
    return useQuery(trpc.assets.list.queryOptions(game && kind && status.enabled ? { key: game, kind } : skipToken));
};

/**
 * Uploads through the hub for most kinds; direct-to-bucket (presign → PUT) for the ones too
 * large to pass through it (marketing packages). `fileProblem` is checked before a byte moves.
 *
 * Presign-then-PUT is two calls, not one tRPC mutation, so this wraps the whole thing — the
 * check, either path, and the raw XHR's progress — in one `useMutation({ mutationFn })` against
 * the vanilla client. `isPending` covers both paths the same way `busy` used to; a successful
 * mutation invalidates through the same global `MutationCache.onSuccess` (`App.tsx`) every other
 * mutation does, so this no longer invalidates by hand.
 */
export const useUploadAsset = () => {
    const client = useTRPCClient();
    const [progress, setProgress] = useState<number | null>(null);

    const mutation = useMutation({
        mutationFn: async ({ game, kind, file }: { game: GameRef; kind: AssetKindView; file: File }) => {
            const problem = fileProblem(kind, file);
            if (problem) throw new Error(problem);
            try {
                if (kind.directUpload) {
                    const contentType = file.type || kind.accept[0]!;
                    const { url } = await client.assets.presignUpload.mutate({
                        key: game,
                        kind: kind.name,
                        filename: file.name,
                        contentType,
                    });
                    await putWithProgress(url, file, contentType, setProgress);
                } else {
                    const form = new FormData();
                    form.set('studio', game.studio);
                    form.set('id', game.id);
                    form.set('kind', kind.name);
                    form.set('file', file);
                    await client.assets.upload.mutate(form);
                }
            } finally {
                setProgress(null);
            }
        },
    });

    const upload = (game: GameRef, kind: AssetKindView, file: File): Promise<void> =>
        mutation.mutateAsync({ game, kind, file });

    return { upload, progress, busy: mutation.isPending };
};

/** Opens a file in a new tab through a short-lived presigned GET — never a stored public URL for a non-image kind. */
export const useOpenAsset = () => {
    const client = useTRPCClient();
    return async (game: GameRef, kind: AssetKind, name: string): Promise<void> => {
        const { url } = await client.assets.presignGet.query({ key: game, kind, name });
        window.open(url, '_blank', 'noopener');
    };
};
